import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaClipboardList, FaFileAlt } from "react-icons/fa";
import { BsClipboardCheckFill } from "react-icons/bs";
import { CiLogout } from "react-icons/ci";
import { FiX } from "react-icons/fi";
import Swal from "sweetalert2";

import ChecklistLayout from "../../../Componentes/ChecklistLayout";
import MenuSuperior from "../../../Componentes/MenuSuperior";
import QuestaoResponderCard from "./components/QuestaoResponderCard";
import AnaliseComentarios, {
  AnaliseComentariosTitle,
} from "./components/Analise/AnaliseComentarios";
import api from "../../../Services/api";
import requestPreSignedUrlAws from "../../../Services/requestPreSignedUrlAws";
import { useOperador } from "../../../Hooks/useOperador";
import { ETipoPermissao } from "../../../Enum/Enum";
import Logo from "../../../Imagens/logoPentagov.jpg";

import "./index.css";

const AuditoriaChecklist = () => {
  const { IDSecao } = useParams();
  const secaoId = Number.isNaN(Number(IDSecao)) ? null : Number(IDSecao);

  const [secao, setSecao] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [questoes, setQuestoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewArquivo, setPreviewArquivo] = useState(null);
  const [previewQuestao, setPreviewQuestao] = useState(null);
  const [previewStatus, setPreviewStatus] = useState("idle");
  const [previewErro, setPreviewErro] = useState("");
  const [previewCarregando, setPreviewCarregando] = useState(false);
  const [questaoSelecionada, setQuestaoSelecionada] = useState(null);
  const [analiseHasComentarios, setAnaliseHasComentarios] = useState(false);
  const [ocultarRespondidas, setOcultarRespondidas] = useState(false);
  const [statusQuestoesDebounced, setStatusQuestoesDebounced] = useState({});
  const [statusQuestoes, setStatusQuestoes] = useState({});
  const [savingMap, setSavingMap] = useState({});
  const salvandoRespostas = Object.values(savingMap).some(Boolean);
  const [finalizando, setFinalizando] = useState(false);
  const { permissaoPrincipal, IDOperador, nome } = useOperador();
  const isAdmin = permissaoPrincipal === ETipoPermissao.ADMIN;
  const idFormatado = String(IDOperador || "").padStart(6, "0");
  const navigate = useNavigate();

  useEffect(() => {
    if (!secaoId) return;

    const carregarDados = async () => {
      try {
        setLoading(true);
        const [secaoResponse, questoesResponse] = await Promise.all([
          api.get(`/chksecao/${secaoId}`),
          api.get("/chkquestaosecao/secao", {
            params: {
              page: 1,
              limit: 100,
              IDSecao: secaoId,
            },
          }),
        ]);

        const secaoData = secaoResponse.data;
        setSecao(secaoData);
        setQuestoes(questoesResponse.data?.data || []);
      } catch (error) {
        console.error("Erro ao carregar checklist da secao", error);
        setQuestoes([]);
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [secaoId]);

  useEffect(() => {
    if (!secao?.IDCliente) {
      setCliente(null);
      return;
    }

    const carregarCliente = async () => {
      try {
        const response = await api.get(`/cliente/${secao.IDCliente}`);
        setCliente(response.data?.Cliente || null);
      } catch (error) {
        console.error("Erro ao buscar cliente:", error);
        setCliente(null);
      }
    };

    carregarCliente();
  }, [secao?.IDCliente]);

  useEffect(() => {
    setStatusQuestoes({});
    setOcultarRespondidas(false);
    setStatusQuestoesDebounced({});
  }, [secaoId]);

  useEffect(() => {
    if (!ocultarRespondidas) {
      setStatusQuestoesDebounced(statusQuestoes);
      return;
    }

    const timer = setTimeout(() => {
      setStatusQuestoesDebounced(statusQuestoes);
    }, 500);

    return () => clearTimeout(timer);
  }, [ocultarRespondidas, statusQuestoes]);

  useEffect(() => {
    setAnaliseHasComentarios(false);
  }, [questaoSelecionada?.IDQuestao_Secao]);

  useEffect(() => {
    setPreviewArquivo(null);
    setPreviewStatus("idle");
    setPreviewErro("");
    setPreviewCarregando(false);
    setPreviewQuestao(null);
  }, [secaoId]);

  const fecharPreview = () => {
    setPreviewArquivo(null);
    setPreviewStatus("idle");
    setPreviewErro("");
    setPreviewCarregando(false);
    setPreviewQuestao(null);
  };

  const getNomeArquivo = (path = "") => {
    if (!path) return "";
    const partes = String(path).split("/");
    return partes[partes.length - 1] || "";
  };

  const getTipoArquivo = (nome = "", url = "") => {
    const alvo = nome || url || "";
    const extensao = alvo.split(".").pop()?.toLowerCase() || "";
    if (extensao === "pdf") return "pdf";
    if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(extensao)) {
      return "imagem";
    }
    return "outro";
  };

  const handleAbrirArquivo = async (objeto, questao) => {
    if (!objeto) return;

    setPreviewStatus("loading");
    setPreviewErro("");
    setQuestaoSelecionada(questao);
    setPreviewQuestao(questao);

    if (objeto?.tipo === 2) {
      const nomeDiretorio = objeto.descricao || objeto.path || "";
      setPreviewArquivo({ nome: nomeDiretorio, tipo: "diretorio" });
      setPreviewCarregando(false);
      setPreviewStatus("ready");
      return;
    }

    const bucket = objeto.nomeBucketAws || null;
    const caminho = objeto.caminho || null;
    const nome = objeto.descricao || getNomeArquivo(objeto.path);

    if (!bucket || !caminho) {
      setPreviewStatus("error");
      setPreviewErro(
        "Nao foi possivel identificar o bucket ou caminho do arquivo."
      );
      return;
    }

    const url = await requestPreSignedUrlAws(
      permissaoPrincipal,
      "getObject",
      bucket,
      caminho,
      nome,
      "inline"
    );

    if (!url || typeof url !== "string") {
      setPreviewStatus("error");
      setPreviewErro("Nao foi possivel obter o link do arquivo.");
      return;
    }

    const tipo = getTipoArquivo(nome, url);
    setPreviewArquivo({ url, nome, tipo });
    setPreviewCarregando(tipo !== "outro");
    setPreviewStatus("ready");
  };

  const questoesAtivas = questoes.filter((questao) => questao?.situacao !== 0);
  const todasAtivasRespondidas = questoesAtivas.every(
    (questao) => statusQuestoes[questao.IDQuestao_Secao],
  );
  const totalAtivas = questoesAtivas.length;
  const totalRespondidas = questoesAtivas.reduce(
    (total, questao) => total + (statusQuestoes[questao.IDQuestao_Secao] ? 1 : 0),
    0,
  );
  const percentualRespondidas = totalAtivas
    ? Math.round((totalRespondidas / totalAtivas) * 100)
    : 0;
  const checklistFinalizado = secao?.situacao === 2;

  const handleFinalizarChecklist = async () => {
    if (!secao?.IDSecao || finalizando) return;

    if (!todasAtivasRespondidas) {
      await Swal.fire({
        title: "Checklist incompleto",
        text: "Existem questões ativas pendentes. Responda todas antes de finalizar.",
        icon: "warning",
      });
      return;
    }

    const confirmacao = await Swal.fire({
      title: "Finalizar checklist?",
      text: "Essa ação é irreversível. Deseja continuar?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, finalizar",
      cancelButtonText: "Cancelar",
      cancelButtonColor: "var(--color-btnCancelar-danger)",
      customClass: {
        cancelButton: "swal-cancel-danger",
      },
    });

    if (!confirmacao.isConfirmed) return;

    try {
      setFinalizando(true);
      const payload = {
        IDSecao: secao.IDSecao,
        IDCliente: secao.IDCliente,
        descricao: secao.descricao,
        ordem: secao.ordem ?? null,
        situacao: 2,
      };
      await api.put(`/chksecao/${secao.IDSecao}`, payload);
      setSecao((prev) => (prev ? { ...prev, situacao: 2 } : prev));
      await Swal.fire(
        "Checklist finalizado",
        "O checklist foi finalizado com sucesso.",
        "success",
      );
    } catch (error) {
      console.error("Erro ao finalizar checklist", error);
      await Swal.fire(
        "Erro",
        "Nao foi possivel finalizar o checklist.",
        "error",
      );
    } finally {
      setFinalizando(false);
    }
  };

  return (
    <ChecklistLayout
      topContent={
        <>
          <div className="mb-4 auditoria-checklist-topo">
            <div className="auditoria-checklist-topo-inner">
              <div className="auditoria-checklist-topo-logo">
                <img className="imgLogo" src={Logo} alt="Logo Pentagov" />
              </div>
              <div className="auditoria-checklist-topo-text">
                <h4>
                  CHECKLIST - AUDITORIA
                </h4>
                <h1>
                  {cliente?.nome} - {cliente?.uf}
                </h1>
                <h2>
                  {secao?.descricao}
                </h2>
              </div>
              <button
                type="button"
                className="auditoria-checklist-topo-back"
                onClick={() => navigate("/Menu")}
              >
                <CiLogout aria-hidden="true" />
                Menu Principal
              </button>
              <div className="auditoria-checklist-topo-menu">
                <MenuSuperior
                  userId={idFormatado}
                  userName={nome}
                  removeMarginRight
                />
              </div>
            </div>
          </div>
        </>
      }
      leftContent={{
        title: (
          <div className="auditoria-checklist-preview-header">
            <h3>
              <FaFileAlt className="auditoria-checklist-title-icon" />
              Visualizador de Arquivos
            </h3>
            <span className="auditoria-checklist-preview-subtitle">
              {previewQuestao?.titulo && `Questão: ${previewQuestao.titulo}`}
            </span>
          </div>
        ),
        actions: (
          !!previewArquivo && (
            <button
              type="button"
              className="auditoria-checklist-preview-close"
              onClick={fecharPreview}
              aria-label="Fechar visualizacao do arquivo"
              title="Fechar visualizacao"
            >
              <FiX />
            </button>
          )
        ),
        content: (
          <div className="auditoria-checklist-preview">
            <div className="auditoria-checklist-preview-body">
              {previewStatus === "idle" && (
                <span>Selecione um arquivo da questao para visualizar aqui.</span>
              )}
              {previewStatus === "loading" && (
                <span>Carregando arquivo...</span>
              )}
              {previewStatus === "error" && <span>{previewErro}</span>}
              {previewStatus === "ready" && previewArquivo && (
                <div className="auditoria-checklist-preview-embed">
                  {previewCarregando && (
                    <div
                      className="auditoria-checklist-preview-overlay"
                      aria-live="polite"
                    >
                      <div className="auditoria-checklist-preview-spinner" />
                      <span>Carregando visualizacao...</span>
                    </div>
                  )}
                  {previewArquivo.tipo === "diretorio" && (
                    <span>{`Diretório: ${previewArquivo.nome}`}</span>
                  )}
                  {previewArquivo.tipo === "pdf" && (
                    <iframe
                      title="Arquivo"
                      src={`${previewArquivo.url}#toolbar=1&navpanes=1&scrollbar=1&zoom=page-fit`}
                      width="100%"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      allowFullScreen
                      onLoad={() => setPreviewCarregando(false)}
                      onError={() => {
                        setPreviewCarregando(false);
                        setPreviewStatus("error");
                        setPreviewErro("Nao foi possivel carregar o arquivo.");
                      }}
                    />
                  )}
                  {previewArquivo.tipo === "imagem" && (
                    <img
                      src={previewArquivo.url}
                      alt={previewArquivo.nome || "Arquivo"}
                      loading="lazy"
                      onLoad={() => setPreviewCarregando(false)}
                      onError={() => {
                        setPreviewCarregando(false);
                        setPreviewStatus("error");
                        setPreviewErro("Nao foi possivel carregar a imagem.");
                      }}
                    />
                  )}
                  {previewArquivo.tipo === "outro" && (
                    <a href={previewArquivo.url} target="_blank" rel="noreferrer">
                      Abrir arquivo
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      }}
      middleContent={{
        title: (
          <div className="auditoria-checklist-questoes-header">
            <div className="auditoria-checklist-questoes-header-top">
              <h3>
                <FaClipboardList className="auditoria-checklist-title-icon" />
                Checklist da Seção
              </h3>
            </div>
            <div className="auditoria-checklist-questoes-header-bottom">
              {secao && (
                <span className="auditoria-checklist-questoes-subtitle">
                  {secao.descricao}
                </span>
              )}
              <span className="auditoria-checklist-questoes-percent">
                {salvandoRespostas ? "Salvando..." : `${percentualRespondidas}%`}
              </span>
            </div>
          </div>
        ),
        progress: percentualRespondidas,
        filters: (
          <div className="auditoria-checklist-filtros">
            <div className="auditoria-checklist-filtros-left">
              <label className="auditoria-checklist-filtro">
                <input
                  type="checkbox"
                  checked={ocultarRespondidas}
                  onChange={(event) => setOcultarRespondidas(event.target.checked)}
                  onMouseDown={() => {
                    const active = document.activeElement;
                    if (active && typeof active.blur === "function") {
                      active.blur();
                    }
                  }}
                  disabled={salvandoRespostas}
                />
                Ocultar respondidas
              </label>
            </div>
            {!loading &&
              questoesAtivas.length > 0 &&
              todasAtivasRespondidas &&
              !checklistFinalizado &&
              isAdmin && (
                <button
                  type="button"
                  className="auditoria-checklist-finalizar"
                  onClick={handleFinalizarChecklist}
                  disabled={loading || finalizando}
                >
                  <BsClipboardCheckFill aria-hidden="true" />
                  {finalizando ? "Finalizando..." : "Finalizar Checklist"}
                </button>
              )}
          </div>
        ),
        content: (
          <div className="auditoria-checklist-questoes">

            {loading && (
              <p className="auditoria-checklist-status">Carregando...</p>
            )}
            {!loading && !questoes.length && (
              <p className="auditoria-checklist-status">
                Nenhuma questao encontrada.
              </p>
            )}
            {!loading && questoes.length > 0 && (
              <ul className="auditoria-checklist-list">
                {questoes
                  .filter((questao) =>
                    ocultarRespondidas
                      ? !statusQuestoesDebounced[questao.IDQuestao_Secao]
                      : true,
                  )
                  .map((questao) => (
                    <li
                      key={questao.IDQuestao_Secao}
                      className={`auditoria-checklist-item ${questaoSelecionada?.IDQuestao_Secao ===
                        questao.IDQuestao_Secao
                        ? "is-selected"
                        : ""
                        }`}
                      onClick={() => setQuestaoSelecionada(questao)}
                    >
                  <QuestaoResponderCard
                    questao={questao}
                    onAbrirArquivo={handleAbrirArquivo}
                    readOnly={checklistFinalizado}
                    isSelected={questaoSelecionada?.IDQuestao_Secao === questao.IDQuestao_Secao}
                    onSavingChange={(id, isSaving) => {
                      setSavingMap((prev) => ({ ...prev, [id]: isSaving }));
                    }}
                    onStatusChange={(id, respondeu) => {
                      setStatusQuestoes((prev) =>
                        prev[id] === respondeu ? prev : { ...prev, [id]: respondeu },
                      );
                    }}
                  />
                    </li>
                  ))}
              </ul>
            )}
          </div>
        ),
      }}
      rightContent={{
        title: (
          <AnaliseComentariosTitle questaoSelecionada={questaoSelecionada} />
        ),
        content: (
          <AnaliseComentarios
            questaoSelecionada={questaoSelecionada}
            onHasAnalisesChange={setAnaliseHasComentarios}
          />
        ),
        hasAlert: analiseHasComentarios,
      }}
    />
  );
};

export default AuditoriaChecklist;
